import { useState } from "react";
import { Printer, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { connectQZTray, getPrinters, printReceipt, isQZConnected } from "@/lib/qz-tray";
import { toast } from "sonner";

export function PrinterTestCard() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [connected, setConnected] = useState(isQZConnected());
  const [printers, setPrinters] = useState<string[]>([]);

  const handleConnect = async () => {
    setIsConnecting(true);
    const success = await connectQZTray();
    setConnected(success);
    
    if (success) {
      const foundPrinters = await getPrinters();
      setPrinters(foundPrinters);
      toast.success(`QZ Tray conectado! ${foundPrinters.length} impressora(s) encontrada(s).`);
    }
    setIsConnecting(false);
  };

  const handleTestPrint = async (printerName: string) => {
    const testContent = 
      "================================\n" +
      "      TESTE DE IMPRESSAO        \n" +
      "================================\n" +
      "Se voce esta lendo isso,\n" +
      "a impressora esta funcionando!\n" +
      "--------------------------------\n" +
      `Impressora: ${printerName}\n` +
      `Data: ${new Date().toLocaleString("pt-BR")}\n` +
      "================================\n\n\n";

    const success = await printReceipt(printerName, testContent);
    if (success) {
      toast.success(`Teste enviado para ${printerName}`);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${connected ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"}`}>
            <Printer className={`h-5 w-5 ${connected ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}`} />
          </div>
          <div>
            <h3 className="font-medium">Status QZ Tray</h3>
            <div className="flex items-center gap-2 text-sm">
              {connected ? (
                <>
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">Conectado</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Desconectado</span>
                </>
              )}
            </div>
          </div>
        </div>
        <Button
          variant={connected ? "outline" : "default"}
          onClick={handleConnect}
          disabled={isConnecting}
        >
          {isConnecting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Conectando...
            </>
          ) : connected ? (
            "Reconectar"
          ) : (
            "Conectar QZ Tray"
          )}
        </Button>
      </div>

      {connected && printers.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Impressoras disponíveis:</p>
          <div className="flex flex-wrap gap-2">
            {printers.map((printer) => (
              <Button
                key={printer}
                variant="outline"
                size="sm"
                onClick={() => handleTestPrint(printer)}
              >
                <Printer className="h-3 w-3 mr-1" />
                {printer}
              </Button>
            ))}
          </div>
        </div>
      )}

      {!connected && (
        <div className="text-sm text-muted-foreground">
          <p>Para usar impressão direta:</p>
          <ol className="list-decimal list-inside mt-2 space-y-1">
            <li>Baixe e instale o QZ Tray em <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer" className="text-primary underline">qz.io/download</a></li>
            <li>Execute o QZ Tray (aparece na bandeja do sistema)</li>
            <li>Clique em "Conectar QZ Tray" acima</li>
          </ol>
        </div>
      )}
    </div>
  );
}
